import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import { Popper } from "@mui/material";
import { useEffect, useRef } from "react";
import { MuiBox, MuiTypography, ToastDisplayType } from "..";
import { TOAST_AUTO_HIDE_DURATION } from "../../helpers/constants";
import { StyledSnackbarContent, ToastWrapper } from "./MuiToast.style";

interface MuiInlineToastProps {
  /** Element to anchor the toast to (e.g., a button ref) */
  anchorEl: HTMLElement | null;
  /** Controls visibility of toast */
  open: boolean;
  /** Text to show inside toast */
  message: string;
  /** Optional toast type */
  type?: ToastDisplayType;
  /** Called when toast auto-hides */
  onClose?: () => void;
  /** Duration in ms before auto-hide */
  autoHideDuration?: number;
  /** Offset distance from anchor (default: 8px below) */
  offset?: number;
}

export const MuiInlineToast: React.FC<MuiInlineToastProps> = ({
  anchorEl,
  open,
  message,
  type = "success",
  onClose,
  autoHideDuration,
  offset = 8,
}) => {
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (open) {
      timerRef.current = setTimeout(() => {
        onClose?.();
      }, autoHideDuration ?? TOAST_AUTO_HIDE_DURATION);
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [open, autoHideDuration, onClose]);

  const renderMessageWithStartIcon = () => {
    return (
      <ToastWrapper>
        <MuiBox className="message-container inline-message-container">
          <TaskAltRoundedIcon className="check-image" />
          <MuiTypography variant="h3" className="message">
            {message}
          </MuiTypography>
        </MuiBox>
      </ToastWrapper>
    );
  };

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement="bottom-end"
      modifiers={[
        {
          name: "offset",
          options: {
            offset: [0, offset], // 8px below icon
          },
        },
      ]}
    >
      <StyledSnackbarContent
        type={type}
        message={renderMessageWithStartIcon()}
        classes={{ root: "custom-snackbar inline-message-snackbar" }}
      />
    </Popper>
  );
};
